import { parseJSON } from './json';
import { API_ENDPOINT } from '../constants';

/**
* Data access + small domain helpers for the external API analytics endpoint
* (VD-725). Shared by the standalone "External API Analytics" page and the
* "API analytics" tab on the client detail page.
*/

export const MAX_RANGE_DAYS = 31;

export const PERIOD_PRESETS = [
    { value: '1h', label: 'Last 1 hour', hours: 1 },
    { value: '24h', label: 'Last 24 hours', hours: 24 },
    { value: '7d', label: 'Last 7 days', hours: 24 * 7 },
    { value: '30d', label: 'Last 30 days', hours: 24 * 30 },
    { value: 'custom', label: 'Custom' },
];

export const INTERVAL_OPTIONS = [
    { value: '', label: 'Auto' },
    { value: 'minute', label: 'Minute' },
    { value: 'hour', label: 'Hour' },
    { value: 'day', label: 'Day' },
];

/** {from, to} ISO strings for a preset, null for 'custom' (caller supplies its own range) */
export const presetRange = (preset) => {
    let def = PERIOD_PRESETS.find((p) => p.value === preset);

    if (!def || !def.hours) {
        return null;
    }

    let to = new Date();
    let from = new Date(to.getTime() - def.hours * 60 * 60 * 1000);

    return { from: from.toISOString(), to: to.toISOString() };
};

const AUTH_ERROR_LABELS = {
    api_key_malformed: 'Malformed API key',
    api_key_not_found: 'API key does not exist',
    api_key_invalid: 'Invalid API key',
    api_key_revoked: 'API key has been revoked',
    api_key_expired: 'API key has expired',
    api_key_disabled: 'API key is disabled',
    ip_not_whitelisted: 'IP is not whitelisted',
    client_not_found: 'Client does not exist',
    missing_credentials: 'Missing credentials',
    basic_malformed: 'Malformed Basic auth header',
    invalid_password: 'Wrong password',
    internal_error: 'Internal error',
};

/**
* Accepts either a bare code or the `authError` object a log carries
* (`{code, message}`), and always returns a string - an unknown code falls
* back to the backend's own message, then to the raw code.
*/
export const authErrorLabel = (authError) => {
    if (!authError) return '-';

    if (typeof authError === 'object') {
        return AUTH_ERROR_LABELS[authError.code] || authError.message || authError.code || '-';
    }

    return AUTH_ERROR_LABELS[authError] || String(authError);
};

export const authMethodLabel = (method, lang) => {
    if (method === 'basic') {
        return 'Basic'.translate(lang);
    }

    if (method === 'apiKey') {
        return 'API key'.translate(lang);
    }

    if (method === 'none') {
        return 'None'.translate(lang);
    }

    return '-';
};

const buildQuery = (filters) => {
    let qs = new URLSearchParams();

    if (filters.from) qs.set('from', filters.from);
    if (filters.to) qs.set('to', filters.to);
    if (filters.interval) qs.set('interval', filters.interval);
    if (filters.clientId) qs.set('clientId', filters.clientId);
    if (filters.apiKeyId) qs.set('apiKeyId', filters.apiKeyId);
    if (filters.route) qs.set('route', filters.route);
    if (filters.method) qs.set('method', filters.method);
    if (filters.replica !== undefined && filters.replica !== null && filters.replica !== '') qs.set('replica', filters.replica);
    // per-client breakdown of the timeseries, ignored by the backend when clientId is set
    if (filters.clientSeries && !filters.clientId) qs.set('clientSeries', filters.clientSeries);

    return qs.toString();
};

/** @returns {Promise<{data: object|null, error: string|null}>} */
export const fetchExternalApiStats = (filters) => {
    let qs = buildQuery(filters || {});

    return fetch(`${API_ENDPOINT}/data/external-api/stats${qs ? '?' + qs : ''}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
    }).then(parseJSON).then(({ result, status }) => {
        if (status >= 200 && status < 300) {
            return { data: result, error: null };
        }

        return { data: null, error: result && result.error ? result.error : 'Failed to load analytics' };
    });
};
